import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Star, MapPin, Calendar, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const Destinations = () => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch trips from public/data/trips.json
    fetch('/data/trips.json')
      .then(response => response.json())
      .then(data => {
        // Filter only active trips
        const activeTrips = data.filter(trip => trip.active);
        setTrips(activeTrips);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading trips:', error);
        toast.error('Failed to load destinations');
        setLoading(false);
      });
  }, []);

  const handleViewDetails = (trip) => {
    console.log('View details for:', trip.destinationName);
    
    // If booking link exists, open it
    if (trip.bookingLink) {
      window.open(trip.bookingLink, '_blank');
    } else {
      toast.info(`Viewing ${trip.destinationName}`, {
        description: 'Contact us for detailed booking information!'
      });
    }
  };

  if (loading) {
    return (
      <section id="destinations" className="py-24 bg-gradient-to-b from-white to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-xl text-gray-600">Loading destinations...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="destinations" className="py-24 bg-gradient-to-b from-white to-orange-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="bg-[#e4e2de] text-[#000000] hover:bg-[#ce9b42] hover:text-white mb-4">
            Popular Destinations
          </Badge>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Explore Paradise
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Handpicked luxury destinations for unforgettable tropical experiences
          </p>
        </div>

        {/* Destinations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {trips.map((trip) => (
            <Card
              key={trip.id}
              className="group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 bg-white"
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={trip.imageUrl}
                  alt={trip.destinationName}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                
                {/* Rating Badge */}
                {trip.rating && (
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 flex items-center space-x-1">
                    <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                    <span className="text-sm font-semibold text-gray-900">{trip.rating}</span>
                  </div>
                )}

                {/* Tags */}
                {trip.tags && trip.tags.length > 0 && (
                  <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                    {trip.tags.slice(0, 2).map((tag, index) => (
                      <span 
                        key={index}
                        className="bg-[#ef571b] text-white text-xs font-medium px-3 py-1 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Title Overlay */}
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-2xl font-bold text-white mb-1">{trip.destinationName}</h3>
                </div>
              </div>

              {/* Content */}
              <CardContent className="p-6">
                <p className="text-gray-600 mb-4 line-clamp-2">{trip.description}</p>
                
                {/* Details */}
                <div className="flex items-center justify-between mb-4 text-sm text-gray-500">
                  {trip.duration && (
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4" />
                      <span>{trip.duration}</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-1">
                    <MapPin className="h-4 w-4" />
                    <span>Tropical</span>
                  </div>
                </div>

                {/* Price & CTA */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div>
                    <p className="text-sm text-gray-500">Starting from</p>
                    <p className="text-2xl font-bold text-[#ef571b]">
                      ${Number(trip.startingPrice).toLocaleString()}
                    </p>
                    {trip.priceRange && (
                      <p className="text-xs text-gray-400 mt-1">{trip.priceRange}</p>
                    )}
                  </div>
                  <Button
                    onClick={() => handleViewDetails(trip)}
                    className="bg-[#ef571b] hover:bg-[#d84915] text-white group/btn"
                  >
                    View Details
                    <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Destinations;